<script setup lang="ts">
import {Label} from "@/core/components/ui/label";
import {Input} from "@/core/components/ui/input";
import {computed, ref, watch} from "vue";

const props = defineProps<{
    modelValue?: string;
    id?: string;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
}>();

const inputId = computed(() => props.id ?? 'french-phone-input');
const display = ref('');
const isFocused = ref(false);

/**
 * Extracts the 9 national digits from a number, whatever its format.
 * Strips the country code (+33 / 33) or the leading 0, then truncates to 9 digits.
 *
 * @param raw - The raw value typed or received (may contain spaces, +, etc.).
 * @returns The national digits (e.g. "612345678").
 */
const nationalDigits = (raw: string): string => {
    let digits = (raw ?? '').replace(/\D/g, '');

    if (digits.startsWith('0033')) {
        digits = digits.slice(4);
    } else if (digits.startsWith('33')) {
        digits = digits.slice(2);
    } else if (digits.startsWith('0')) {
        digits = digits.slice(1);
    }

    return digits.slice(0, 9);
};

/**
 * Formats a number for display, grouped as "6 12 34 56 78".
 *
 * @param raw - The raw value to format.
 * @returns The formatted string with spaces (1-2-2-2-2 groups).
 */
const format = (raw: string): string => {
    const digits = nationalDigits(raw);
    return [digits.slice(0, 1), digits.slice(1, 3), digits.slice(3, 5), digits.slice(5, 7), digits.slice(7, 9)]
        .filter(Boolean)
        .join(' ');
};

/**
 * Sync from the parent, except while the user is editing.
 */
watch(() => props.modelValue, (val) => {
    if (isFocused.value) {
        return;
    }

    display.value = format(val ?? '');
}, {immediate: true});

/**
 * Fired on each keystroke: emits the E.164 value (+33…) when the number
 * is complete (9 digits), otherwise an empty string.
 */
const onInput = (): void => {
    const digits = nationalDigits(display.value);
    emit('update:modelValue', digits.length === 9 ? `+33${digits}` : '');
};

/**
 * Fired on blur: releases the editing lock and reformats the displayed value.
 */
const onBlur = (): void => {
    isFocused.value = false;
    display.value = format(display.value);
};
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <Label :for="inputId">Numéro de mobile</Label>
    <div class="flex rounded-lg border border-input shadow-sm focus-within:ring-1 focus-within:ring-ring">
      <span
        class="flex items-center px-3 border-r border-input select-none rounded-l-lg bg-muted text-muted-foreground"
      >
        +33
      </span>
      <Input
        :id="inputId"
        v-model="display"
        type="tel"
        inputmode="numeric"
        placeholder="6 12 34 56 78"
        class="border-0 shadow-none rounded-l-none focus-visible:ring-0 text-base"
        @input="onInput"
        @focus="isFocused = true"
        @blur="onBlur"
      />
    </div>
  </div>
</template>
